import {
  createAsyncThunk,
  createSelector,
  createSlice,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import chronosApi from '@chronos/api-client/chronosApi';
import trelloApi from '@chronos/api-client/trelloApi';
import * as utils from '@chronos/utils';
import socketHelper from '@chronos/utils/socketHelper';
import usersApi from './usersApi';

const REDUCER_NAME = 'board';

const initialState = {
  chronosJWT: '',
  isLoading: true,
  isInitialized: false,
  isLoaded: false,
  sidebarIsCollapsed: true,
  headerTitle: '',
  currentBoardId: null,
  boards: [],
  onlineUsers: {},
  liveWorklogs: [],
  ownLiveWorklogs: [],
  boardLiveWorklogs: [],
  userPermissions: null,
  users: [],
  isTimerRunning: false,
  lists: [],
  cards: [],
  cardsStats: {},
  cardsEstimates: [],
};

export const fetchBoards = createAsyncThunk(
  `${REDUCER_NAME}/fetchBoards`,
  (userId) =>
    trelloApi.getMemberBoards({
      params: { memberId: userId },
    })
);

const usersAdapter = createEntityAdapter();
const initialUserState = usersAdapter.getInitialState();

export const initialize = createAsyncThunk(
  `${REDUCER_NAME}/initialize`,
  async (payload, thunkAPI) => {
    const { userId, boardId, organizationId, trelloIdPlugin } = payload;
    const { dispatch } = thunkAPI;

    log.info('send request initialize');
    const trelloToken = window.localStorage.getItem('trello_token');
    const result = await chronosApi.initialize({
      body: {
        trelloToken,
        idBoard: boardId,
        idMember: userId,
        idOrganization: organizationId,
        trelloIdPlugin,
      },
    });

    const users = [];
    [...result.boardMemberships, ...result.organizationMemberships].forEach(
      ({ member }) => {
        if (!users.find((item) => item.id === member.id)) {
          users.push(member);
        }
      }
    );
    const normalizedUsers = usersAdapter.setAll(initialUserState, users);
    dispatch(
      usersApi.util.upsertQueryData(
        'getUsers',
        { boardId, organizationId },
        normalizedUsers
      )
    );

    log.info('send request finish!');
    chronosApi.setJWT(result.jwtToken);
    return result;
  }
);

export const fetchCardsStats = createAsyncThunk(
  `${REDUCER_NAME}/fetchCardsStats`,
  ({ profileId, boardId, cardIds }) => {
    const body = { cardIds, boardId, withEstimates: true };
    if (profileId) {
      body.accountId = profileId;
    }
    return chronosApi.getCardsStats({ body });
  }
);

export const fetchListsWithCards = createAsyncThunk(
  `${REDUCER_NAME}/fetchListsWithCards`,
  async ({ boardId, fetchStats }, { dispatch }) => {
    const lists = await trelloApi.getLists({ params: { boardId } });
    const cards = await trelloApi.getVisibleCards({ params: { boardId } });

    if (fetchStats) {
      const cardIds = cards.map((card) => card.id);
      dispatch(fetchCardsStats({ boardId, cardIds }));
    }

    return {
      lists,
      cards: cards.map((card) => ({
        ...card,
        labels: card.labels.map((label) => ({
          ...label,
          colorGroup: utils.getLabelHexColor(label.color),
        })),
      })),
    };
  }
);

export const saveLiveWorklog = createAsyncThunk(
  `${REDUCER_NAME}/saveLiveWorklog`,
  ({ boardId, cardId, state }) => {
    const body = {
      cardId,
      boardId,
      fastResponse: true,
      state,
    };
    socketHelper.getSocket().emit('testEvent');
    setTimeout(() => {
      socketHelper.getSocket().emit('syncLiveWorklogs', {
        boardId,
        // memberId,
      });
    }, 1500);

    return chronosApi.setTimerState({ body });
  }
);

export const boardSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    setIsTimerRunning: (state, action) => {
      state.isTimerRunning = action.payload;
    },
    setIsInitialized: (state, action) => {
      state.isInitialized = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setLoaded: (state, action) => {
      state.isLoaded = action.payload;
    },
    setCurrentBoardId: (state, action) => {
      state.currentBoardId = action.payload;
    },
    setSidebarIsCollapsed: (state, action) => {
      state.sidebarIsCollapsed = action.payload;
    },
    setHeaderTitle: (state, action) => {
      state.headerTitle = action.payload;
    },
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
    },
    setLiveWorklogs: (state, action) => {
      const { roomType, liveWorklogs } = action.payload;
      if (roomType === 'board') {
        state.boardLiveWorklogs = liveWorklogs;
        state.liveWorklogs = liveWorklogs;
      }
      if (roomType === 'member') {
        state.ownLiveWorklogs = liveWorklogs;
      }
    },
    resetState: (state) => {
      Object.assign(state, {
        isLoaded: false,
        isInitialized: false,
        headerTitle: '',
        onlineUsers: {},
        liveWorklogs: [],
        ownLiveWorklogs: [],
        boardLiveWorklogs: [],
        userPermissions: null,
        users: [],
        boardMemberships: [],
        organizationMemberships: [],
        isTimerRunning: false,

        lists: [],
        cards: [],
        cardsStats: {},
        cardsEstimates: [],
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBoards.fulfilled, (state, action) => {
      state.boards = action.payload;
    });
    builder.addCase(fetchListsWithCards.fulfilled, (state, action) => {
      const { cards } = action.payload;
      const lists = action.payload.lists.map((list) => ({
        ...list,
        cards: cards.filter((card) => card.idList === list.id),
      }));
      Object.assign(state, { lists, cards });
    });
    builder.addCase(fetchCardsStats.fulfilled, (state, action) => {
      const { cardsStats, estimates } = action.payload;
      Object.assign(state, { cardsStats, cardsEstimates: estimates });
    });
    builder.addCase(initialize.fulfilled, (state, action) => {
      const {
        jwtToken: chronosJWT,
        boardUserPermissions: userPermissions,
        boardMemberships,
        organizationMemberships,
        // isUserActivate, isLicenseExpired, isShowBilling,
      } = action.payload;

      const users = [...organizationMemberships, ...boardMemberships].reduce(
        (acc, item) => {
          acc[item.member.id] = item.member;
          return acc;
        },
        {}
      );

      Object.assign(state, {
        chronosJWT,
        users,
        userPermissions,
      });
    });
  },
});

const selectSelf = (state) => state.shared[REDUCER_NAME];

export const selectIsInitialized = (state) => selectSelf(state).isInitialized;
export const selectChronosJWT = (state) => selectSelf(state).chronosJWT;
export const selectBoards = (state) => selectSelf(state).boards;
export const selectCurrentBoardId = (state) => selectSelf(state).currentBoardId;
export const selectLiveWorklogsByType = createSelector(
  [selectSelf, (state, props = {}) => props.roomType],
  (state, roomType) => {
    return roomType === 'member'
      ? state.ownLiveWorklogs
      : state.boardLiveWorklogs;
  }
);
export const selectLiveWorklogs = createSelector(
  [selectSelf, (state) => state.app.profile, (state, props = {}) => props],
  (state, profile, props) => {
    let { liveWorklogs } = state;
    if (props.isSelfWorklogs) {
      liveWorklogs = liveWorklogs.filter(
        (worklog) => worklog.memberId === profile.id
      );
    }
    if (props.boardId) {
      liveWorklogs = liveWorklogs.filter(
        (worklog) => worklog.boardId === props.boardId
      );
    }
    return liveWorklogs;
  }
);
export const selectHeaderTitle = (state) => selectSelf(state).headerTitle;
export const selectIsLoading = (state) => selectSelf(state).isLoading;
export const selectIsLoaded = (state) => selectSelf(state).isLoaded;
export const selectSidebarIsCollapsed = (state) =>
  selectSelf(state).sidebarIsCollapsed;
export const selectOnlineUsers = (state) => selectSelf(state).onlineUsers;
export const selectUserPermissions = (state) =>
  selectSelf(state).userPermissions;
export const selectIsTimerRunning = (state) => selectSelf(state).isTimerRunning;
export const selectCards = (state) => selectSelf(state).cards;
export const selectLists = (state) => selectSelf(state).lists;
export const selectCardsStats = (state) => selectSelf(state).cardsStats;
export const selectCardsEstimates = (state) => selectSelf(state).cardsEstimates;

export const selectListsWithUserCards = createSelector(
  [selectLists, (_, props) => props.profile, (_, props = {}) => props.isMyOnly],
  (lists, profile, isMyOnly = false) =>
    isMyOnly
      ? lists.map((list) => ({
          ...list,
          cards: list.cards.filter((card) =>
            card.idMembers.includes(profile.id)
          ),
        }))
      : lists
);

export const { actions, reducer } = boardSlice;

export const {
  setLoading,
  setSidebarIsCollapsed,
  setHeaderTitle,
  setLiveWorklogs,
  setOnlineUsers,
  setCurrentBoardId,
  setIsTimerRunning,
  setIsInitialized,
} = actions;

export default reducer;
